/**
 * Field rules and input normalisers that more than one feature's form needs.
 *
 * Anything domain-specific (an IFSC code, a UPI address, a date of birth)
 * stays in that feature's own `validation.ts`. What lives here is only what
 * the brand form and the client form both apply — kept in one place so the
 * two can never end up disagreeing about what a phone number looks like.
 *
 * The normalisers run on every keystroke, so a field can only ever hold a
 * shape the rules below recognise. They coerce rather than reject: nothing a
 * normaliser sees is thrown away that the user meant to keep.
 */

/**
 * Deliberately stricter than "something, an @, something": the local part may
 * not start, end or double up on dots, the domain needs at least one label
 * before a two-letter-or-longer TLD. That rejects the near-misses people
 * actually type — `ravi@nutro`, `ravi@.in`, `ravi.@nutro.in`.
 */
export const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/** An Indian mobile number: ten digits, and the first is 6, 7, 8 or 9. */
const MOBILE_PATTERN = /^[6-9]\d{9}$/;

/** Six digits, and no Indian PIN code starts with a zero. */
const PINCODE_PATTERN = /^[1-9]\d{5}$/;

export const digitsOf = (value: string) => value.replace(/\D/g, "");

/** Empty string means the value is fine — the shape every rule here returns. */
export const emailError = (value: string): string =>
  EMAIL_PATTERN.test(value) ? "" : "Enter a valid email address";

/**
 * Phone and WhatsApp share a rule, and both say which half of it failed —
 * "must be 10 digits" and "must start with…" send you to different fixes.
 */
export const mobileError = (label: string, value: string): string => {
  const digits = digitsOf(value);

  if (digits.length !== 10) return `${label} must be 10 digits`;
  if (!MOBILE_PATTERN.test(digits))
    return `${label} must start with 6, 7, 8 or 9`;

  return "";
};

/**
 * Digits only, capped at the ten of an Indian mobile number. A pasted number
 * that still carries its country code or a trunk `0` is trimmed back to the
 * subscriber digits rather than truncated from the wrong end.
 */
export const normalisePhoneInput = (raw: string): string => {
  let digits = digitsOf(raw);

  if (digits.length > 10 && digits.startsWith("91")) digits = digits.slice(2);
  if (digits.length > 10 && digits.startsWith("0")) digits = digits.slice(1);

  return digits.slice(0, 10);
};

/**
 * Drops the country code the API sends, which is how every phone field in the
 * app holds a number - the `+91` is rendered beside the value, not stored in
 * it. Anything that is not an Indian mobile is left exactly as it arrived.
 *
 * The mirror of `normalisePhoneInput`: that one is what a person types, this
 * one is what a response carries.
 */
export const toNationalNumber = (raw: string): string => {
  const value = (raw ?? "").trim();
  return /^\+91\d{10}$/.test(value) ? value.slice(3) : value;
};

/**
 * Puts the country code back on for the API, which takes numbers in E.164.
 * The mirror of `toNationalNumber`, and applied only to the ten digits the
 * form collects - anything already carrying a `+` goes up untouched.
 */
export const toInternationalNumber = (raw: string): string => {
  const value = (raw ?? "").trim();
  if (value.startsWith("+")) return value;

  const digits = digitsOf(value);
  return digits.length === 10 ? `+91${digits}` : digits;
};

export const pincodeError = (value: string): string =>
  PINCODE_PATTERN.test(value) ? "" : "Enter a valid 6-digit pincode";

/** No address contains a space, and case carries no meaning in one either. */
export const normaliseEmailInput = (raw: string): string =>
  raw.replace(/\s+/g, "").toLowerCase();

/** Digits only, stopped at the six an Indian PIN code has. */
export const normalisePincodeInput = (raw: string): string =>
  digitsOf(raw).slice(0, 6);

/**
 * "john", "JOHN" and "jOhN" all become "John": every word capitalised, the
 * rest lower-cased, so names reach the invoice in one consistent shape.
 * Hyphens and apostrophes start a word too, which keeps "Mary-Jane" and
 * "D'Souza" reading correctly.
 */
export const toNameCase = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(
      /(^|[\s'-])([a-z])/g,
      (_match, lead: string, letter: string) => lead + letter.toUpperCase()
    );
